import * as React from 'react'
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { settingsService } from '../../core/services'
import * as constants from '../../static/constants'
import { getStripePublicKey } from '../../utils'

const stripePromise = loadStripe(getStripePublicKey())

interface IProps {
  setStripeParamsGetter: any
  hideLabel?: boolean
  setUseRegisteredCard: any
  useRegisteredCard: boolean
}

const createOptions = () => {
  return {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: '16px',
        color: constants.COLORS.black,
        letterSpacing: '0.5px',
        fontFamily: 'Source Code Pro, monospace',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: constants.COLORS.caution,
      },
    },
  }
}

const StripeCardSelect: React.FC<IProps> = props => {
  const stripe = useStripe()
  const elements = useElements()
  const [card, setCard] = React.useState(null)

  const getCard = async () => {
    if (props.useRegisteredCard) {
      const { card: currentCard } = await settingsService.getCard()
      setCard(currentCard)
    }
  }

  React.useEffect(() => {
    getCard()
  }, [])

  const getStripeParams = React.useCallback(
    async secret => {
      const result = await stripe.confirmCardPayment(
        secret,
        props.useRegisteredCard
          ? {}
          : {
              payment_method: { card: elements.getElement(CardElement) },
            }
      )
      return result
    },
    [stripe, elements, card, props.useRegisteredCard]
  )

  React.useEffect(() => {
    props.setStripeParamsGetter(() => getStripeParams)
  }, [getStripeParams])

  return (
    <CardForm>
      {!props.hideLabel && (
        <div className="CardForm_Label">
          {I18n.t('generic.payment')}
          <img src="/images/stripe-logo-1.svg" className="stripe-logo" alt="Stripe Logo" />
        </div>
      )}

      <Radio className={props.hideLabel ? 'No_Margin' : ''}>
        {card && (
          <label>
            <input
              type="radio"
              name="useRegisteredCard"
              value=""
              checked={props.useRegisteredCard}
              onChange={() => props.setUseRegisteredCard(true)}
            />
            <span className="CardForm_RegisteredCard">
              <span>{I18n.t('card.registered_cards')} -</span>
              <span>{card.brand}</span>
              <span>{I18n.t('card.card_end')}: </span>
              <span>{card.last4}</span>
              <span className="exp">{I18n.t('generic.expiration_date')}: </span>
              <span>
                {card.exp_month}/{card.exp_year}
              </span>
            </span>
          </label>
        )}
        <label>
          <input
            type="radio"
            name="useRegisteredCard"
            value=""
            checked={!props.useRegisteredCard}
            onChange={() => props.setUseRegisteredCard(false)}
          />
          <span>{I18n.t('card.new_card')}</span>
        </label>
      </Radio>
      {!props.useRegisteredCard && <CardElement options={createOptions()} />}
    </CardForm>
  )
}

const CardForm = styled.div`
  .CardForm_RegisteredCard {
    span + span {
      margin-left: 8px;
    }

    span.exp {
      margin-left: 24px;
    }

    & + .Button {
      margin-top: 16px;
    }
  }

  .CardForm_Label {
    display: flex;
    align-items: center;
    margin-top: 24px;
    font-size: 18px;
    font-weight: bold;
    .stripe-logo {
      width: 120px;
      height: auto;
      margin-left: 16px;
    }
  }

  .StripeElement {
    display: block;
    margin: 10px 0 20px 0;
    max-width: 500px;
    padding: 10px 14px;
    box-shadow: rgba(50, 50, 93, 0.14902) 0px 1px 3px, rgba(0, 0, 0, 0.0196078) 0px 1px 0px;
    border-radius: 4px;
    background: white;
  }

  .StripeElement--focus {
    box-shadow: rgba(50, 50, 93, 0.109804) 0px 4px 6px, rgba(0, 0, 0, 0.0784314) 0px 1px 3px;
    transition: all 150ms ease;
  }

  .StripeElement.PaymentRequestButton {
    padding: 0;
  }
`

const Radio = styled.div`
  margin: 24px 0 0;
  &.No_Margin {
    margin: 0;
  }
  > h3 {
    display: block;
    margin-bottom: 12px;
    font-weight: bold;
    font-size: 14px;
  }

  > label {
    display: block;
    position: relative;
    cursor: pointer;
    padding-left: 32px;

    &:hover {
      opacity: 0.75;
    }

    input {
      opacity: 0;
      height: 0;
      width: 0;
    }

    input + span {
      font-size: 14px;

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        margin: auto;
      }

      &::before {
        width: 18px;
        height: 18px;
        border: solid 1px ${constants.COLORS.divider};
        border-radius: 50%;
      }
    }

    input:checked + span {
      &::before {
        width: 18px;
        height: 18px;
        border: solid 1px ${constants.COLORS.primary};
        border-radius: 50%;
      }

      &::after {
        left: 4px;
        width: 10px;
        height: 10px;
        background-color: ${constants.COLORS.primary};
        border-radius: 50%;
      }
    }
  }

  > label + label {
    margin-top: 16px;
  }
`

const StripeCardSelectWithProvider: React.FC<{
  setStripeParamsGetter: any
  hideLabel?: boolean
  setUseRegisteredCard: any
  useRegisteredCard: boolean
}> = props => {
  return (
    <Elements stripe={stripePromise}>
      <StripeCardSelect
        setStripeParamsGetter={props.setStripeParamsGetter}
        setUseRegisteredCard={props.setUseRegisteredCard}
        useRegisteredCard={props.useRegisteredCard}
        hideLabel={props.hideLabel}
      />
    </Elements>
  )
}

export default StripeCardSelectWithProvider
